define("ProductStructuredData.Helper", [], function () {
  "use strict";

  var PRODUCT_TYPE = "https://schema.org/Product";
  var BRAND_TYPE = "https://schema.org/Brand";
  var OFFER_TYPE = "https://schema.org/Offer";
  var AGGREGATE_OFFER_TYPE = "https://schema.org/AggregateOffer";
  var AGGREGATE_RATING_TYPE = "https://schema.org/AggregateRating";
  var PROPERTY_VALUE_TYPE = "https://schema.org/PropertyValue";
  var IN_STOCK = "https://schema.org/InStock";
  var NEW_CONDITION = "https://schema.org/NewCondition";

  function trim(value) {
    return value === null || value === undefined ? "" : String(value).trim();
  }

  function get(model, key) {
    return model && typeof model.get === "function"
      ? model.get(key)
      : model && model[key];
  }

  function stripHtml(value) {
    var html = trim(value);
    var element;

    if (!html) return "";

    if (typeof document !== "undefined" && document.createElement) {
      element = document.createElement("div");
      element.innerHTML = html;
      return trim(element.textContent || element.innerText).replace(/\s+/g, " ");
    }

    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\s+/g, " ")
      .trim();
  }

  function absoluteUrl(value, origin) {
    var url = trim(value);
    var base = trim(origin).replace(/\/+$/, "");

    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return encodeURI(url);
    if (url.indexOf("//") === 0) {
      return encodeURI((base.indexOf("https:") === 0 ? "https:" : "http:") + url);
    }
    return encodeURI(base + "/" + url.replace(/^\/+/, ""));
  }

  function numberValue(value) {
    if (value === null || value === undefined || value === "") return null;
    var parsed = typeof value === "number" ? value : Number(value);
    return isFinite(parsed) ? parsed : null;
  }

  function cleanBrand(value) {
    return trim(value).replace(/\s+(Tires|Wheels)\s*$/i, "").trim();
  }

  function addProperty(properties, name, value) {
    var values = Array.isArray(value) ? value : [value];

    values.forEach(function (currentValue) {
      var normalized = trim(currentValue);
      if (!normalized || normalized.toLowerCase() === "none" || normalized === "&nbsp;") {
        return;
      }
      properties.push({ name: name, value: normalized });
    });
  }

  function getFirst(item, fields) {
    var result = "";
    fields.some(function (field) {
      result = trim(get(item, field));
      return !!result;
    });
    return result;
  }

  function getTireSize(item) {
    var configured = getFirst(item, ["custitemrs_tire_size"]);
    var width;
    var aspectRatio;
    var diameter;

    if (configured) return configured;

    width = trim(get(item, "custitemrs_tire_width"));
    aspectRatio = trim(get(item, "custitemrs_tire_aspect_ratio"));
    diameter = trim(get(item, "custitemrs_tire_inside_diameter"));

    return width && aspectRatio && diameter
      ? width + "/" + aspectRatio + "R" + diameter
      : "";
  }

  function getAdditionalProperties(item) {
    var properties = [];
    var tireSize = getTireSize(item);

    addProperty(
      properties,
      "Size",
      tireSize || getFirst(item, ["custitemrs_p_wheel_size"])
    );
    addProperty(properties, "Load Index", get(item, "custitemrs_tire_load_index"));
    addProperty(properties, "Speed Rating", get(item, "custitemrs_tire_speed_rating"));
    addProperty(
      properties,
      "Sidewall",
      getFirst(item, ["custitemrs_tire_sidewall_1", "custitemrs_tire_sidewall_2"])
    );
    addProperty(properties, "Service Type", get(item, "custitemrs_tire_service_type"));
    addProperty(properties, "Bolt Pattern", get(item, "custitemrs_wheel_bolt_patterns"));
    addProperty(
      properties,
      "Finish",
      getFirst(item, ["custitemrs_wheel_finish", "custitemrs_wheel_finish_category"])
    );
    addProperty(
      properties,
      "Width",
      getFirst(item, [
        "custitemrs_wheel_width_min",
        "custitemrs_wheel_min_width",
        "custitemrs_tire_wheel_width_min",
      ])
    );
    addProperty(
      properties,
      "Diameter",
      getFirst(item, [
        "custitemrs_wheel_diameter_min",
        "custitemrs_wheel_min_diameter",
      ])
    );
    addProperty(
      properties,
      "Offset",
      getFirst(item, [
        "custitemrs_wheel_offset_min",
        "custitemrs_p_wheel_offset_category",
      ])
    );
    addProperty(properties, "Hub Bore", get(item, "custitemrs_wheel_centerbore"));

    return properties;
  }

  function getGtin(item) {
    var value = getFirst(item, ["upccode", "upcCode", "gtin"]);
    var digits = value.replace(/[^0-9]/g, "");
    var allowedLengths = [8, 12, 13, 14];

    if (allowedLengths.indexOf(digits.length) === -1) return null;

    return {
      property: "gtin" + digits.length,
      value: digits,
    };
  }

  function getDescription(options, item) {
    var values = [
      options.description,
      get(item, "storedetaileddescription"),
      get(item, "storedescription"),
    ];
    var result = "";

    values.some(function (value) {
      result = stripHtml(value);
      return !!result;
    });

    return result;
  }

  function getImages(options) {
    var images = options.images || [];
    var first = images[0] || {};
    return absoluteUrl(first.url || options.image, options.origin);
  }

  function getOffer(options, canonicalUrl) {
    var price = options.price || {};
    var exactPrice = numberValue(price.price);
    var minPrice = price.min ? numberValue(price.min.price) : null;
    var maxPrice = price.max ? numberValue(price.max.price) : null;
    var isPriceRange = minPrice !== null && maxPrice !== null;
    var offer = {
      type: isPriceRange ? AGGREGATE_OFFER_TYPE : OFFER_TYPE,
      schemaType: isPriceRange ? "AggregateOffer" : "Offer",
      url: canonicalUrl,
      priceCurrency: trim(options.currency) || "CAD",
      availability: IN_STOCK,
      itemCondition: NEW_CONDITION,
      isPriceRange: isPriceRange,
      hasPrice: exactPrice !== null || isPriceRange,
    };

    if (isPriceRange) {
      offer.lowPrice = minPrice;
      offer.highPrice = maxPrice;
    } else if (exactPrice !== null) {
      offer.price = exactPrice;
    }

    return offer;
  }

  function build(options) {
    var item = options.item || {};
    var canonicalUrl = absoluteUrl(options.canonicalUrl || options.itemUrl, options.origin);
    var ratingValue = numberValue(
      get(item, "_rating") || get(item, "rating") || options.ratingValue
    );
    var reviewCount = numberValue(
      get(item, "_ratingsCount") || options.reviewCount || 0
    );
    var gtin = getGtin(item);
    var mode = trim(options.structuredDataType);
    var result = {
      isMicrodata: mode !== "JSON-LD",
      isJsonLd: mode === "JSON-LD",
      type: PRODUCT_TYPE,
      id: canonicalUrl ? canonicalUrl.replace(/#.*$/, "") + "#product" : "",
      url: canonicalUrl,
      name: trim(options.name),
      description: getDescription(options, item),
      sku: trim(options.sku),
      brand: cleanBrand(get(item, "custitemrs_brand")),
      model: trim(get(item, "custitemrs_model")),
      image: getImages(options),
      mpn: getFirst(item, ["mpn", "manufacturerpartnumber"]),
      gtin: gtin,
      offer: getOffer(options, canonicalUrl),
      additionalProperties: getAdditionalProperties(item),
      hasRating: reviewCount > 0 && ratingValue !== null && ratingValue > 0,
      ratingValue: ratingValue,
      reviewCount: reviewCount,
      bestRating: 5,
    };

    result.hasUrl = !!result.url;
    result.hasName = !!result.name;
    result.hasDescription = !!result.description;
    result.hasSku = !!result.sku;
    result.hasBrand = !!result.brand;
    result.hasModel = !!result.model;
    result.hasImage = !!result.image;
    result.hasMpn = !!result.mpn;
    result.hasGtin = !!result.gtin;
    result.hasOffer = options.includeOffer !== false && result.offer.hasPrice;
    result.hasAdditionalProperties = result.additionalProperties.length > 0;

    return result;
  }

  function toJsonLd(product, childJsonLd) {
    var jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": product.id,
      url: product.url,
      name: product.name,
    };
    var offer;

    if (product.hasDescription) jsonLd.description = product.description;
    if (product.hasSku) jsonLd.sku = product.sku;
    if (product.hasBrand) {
      jsonLd.brand = { "@type": "Brand", name: product.brand };
    }
    if (product.hasModel) jsonLd.model = product.model;
    if (product.hasImage) jsonLd.image = product.image;
    if (product.hasMpn) jsonLd.mpn = product.mpn;
    if (product.hasGtin) jsonLd[product.gtin.property] = product.gtin.value;

    if (product.hasOffer) {
      offer = {
        "@type": product.offer.schemaType,
        url: product.offer.url,
        priceCurrency: product.offer.priceCurrency,
        availability: product.offer.availability,
        itemCondition: product.offer.itemCondition,
      };
      if (product.offer.isPriceRange) {
        offer.lowPrice = product.offer.lowPrice;
        offer.highPrice = product.offer.highPrice;
      } else {
        offer.price = product.offer.price;
      }
      jsonLd.offers = offer;
    }

    if (product.hasRating) {
      jsonLd.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: product.ratingValue,
        reviewCount: product.reviewCount,
        bestRating: product.bestRating,
      };
    }

    if (childJsonLd && Array.isArray(childJsonLd.review)) {
      jsonLd.review = childJsonLd.review;
    }

    if (product.hasAdditionalProperties) {
      jsonLd.additionalProperty = product.additionalProperties.map(function (property) {
        return {
          "@type": "PropertyValue",
          name: property.name,
          value: property.value,
        };
      });
    }

    return jsonLd;
  }

  return {
    types: {
      product: PRODUCT_TYPE,
      brand: BRAND_TYPE,
      offer: OFFER_TYPE,
      aggregateOffer: AGGREGATE_OFFER_TYPE,
      aggregateRating: AGGREGATE_RATING_TYPE,
      propertyValue: PROPERTY_VALUE_TYPE,
    },
    build: build,
    toJsonLd: toJsonLd,
  };
});
